import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import { Box, FormControlLabel, IconButton } from '@mui/material';
import React from 'react';
import type { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import type Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { getSelectedInvestmentAmount } from '../../pages/fundUpdate/fundUpdateUtils';
import { Palette } from 'Palette';
import { StyledCheckbox } from '../styles/StyledInputs';
import { StyledTypography } from '../styles/styles';
import InvestmentEntitySelectionWrapper from './InvestmentEntitySelectionWrapper';

export interface FundUpdateAllocationSelectionProps {
  investor?: Investor;
  allocationsData?: InvestorAllocationsResponse;
  selectedEntityIds: Set<string>;
  allocationsLoading: boolean;
  allocationsError?: string;
  onEntitySelection: (entityId: string, selected: boolean) => void;
}

const FundUpdateAllocationSelection: React.FC<FundUpdateAllocationSelectionProps> = ({
  investor,
  allocationsData,
  selectedEntityIds,
  allocationsLoading,
  allocationsError,
  onEntitySelection,
}) => {
  if (!investor) {
    return null;
  }

  if (allocationsLoading) {
    return <InvestmentEntitySelectionWrapper heading="Loading allocation data..." />;
  }

  if (allocationsError) {
    return (
      <InvestmentEntitySelectionWrapper heading="Investment Entity Selection">
        <StyledTypography color="error">{allocationsError}</StyledTypography>
        <StyledTypography sx={{ mt: 2 }}>
          Chart will show data for the full committed investment amount.
        </StyledTypography>
      </InvestmentEntitySelectionWrapper>
    );
  }

  if (!allocationsData) {
    return null;
  }

  const hasAllocations = allocationsData.allocations.length > 0;

  if (!hasAllocations) {
    return (
      <InvestmentEntitySelectionWrapper heading="Investment Entity Selection">
        <StyledTypography>
          No investment entity allocations found for this opportunity.
        </StyledTypography>
        <StyledTypography sx={{ mt: 1 }}>
          Chart will show data for the full committed investment amount.
        </StyledTypography>
      </InvestmentEntitySelectionWrapper>
    );
  }

  const observerEntityIdSet = new Set<string>(
    ((investor as Investor & { __observerEntityIds?: string[] }).__observerEntityIds ||
      []) as string[],
  );

  const entityGroups = new Map<
    string,
    { totalAmount: number; entity?: { name: string; id: string }; hasObserverRole: boolean }
  >();

  allocationsData.allocations.forEach((allocation) => {
    const entityId = allocation.investmentEntityId;
    const amount = parseFloat(allocation.allocatedAmount);
    const hasObserverRole =
      (allocation as { contactRole?: string }).contactRole === 'REPRESENTATIVE' ||
      observerEntityIdSet.has(entityId);
    const entity = {
      name: allocation.investmentEntityName,
      id: allocation.investmentEntityId,
    };

    if (entityGroups.has(entityId)) {
      entityGroups.get(entityId)!.totalAmount += amount;
      entityGroups.get(entityId)!.hasObserverRole =
        entityGroups.get(entityId)!.hasObserverRole || hasObserverRole;
    } else {
      entityGroups.set(entityId, {
        totalAmount: amount,
        entity,
        hasObserverRole,
      });
    }
  });

  const selectedAmount = getSelectedInvestmentAmount(
    investor,
    allocationsData,
    selectedEntityIds,
    undefined,
  );

  return (
    <InvestmentEntitySelectionWrapper heading="Investment Entity Selection">
      <StyledTypography sx={{ mb: 2 }}>
        Select which investment entities to include in the projected cashflow analysis.
      </StyledTypography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {Array.from(entityGroups.entries()).map(
          ([entityId, { totalAmount, entity, hasObserverRole }]) => {
            const isSelected = selectedEntityIds.has(entityId);
            const entityName = entity?.name || `Entity ${entityId}`;

            return (
              <FormControlLabel
                key={entityId}
                control={
                  <StyledCheckbox
                    checked={isSelected}
                    onChange={(e) => onEntitySelection(entityId, e.target.checked)}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        minWidth: 0,
                      }}
                    >
                      <StyledTypography>
                        {hasObserverRole ? 'Representative: ' : ''}
                        {entityName}
                      </StyledTypography>
                      {hasObserverRole && (
                        <IconButton
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                          aria-label="Observer allocation"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <SupervisedUserCircleIcon
                            sx={{
                              color: Palette.default.text,
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>
                    <StyledTypography sx={{ fontWeight: 600 }}>
                      {ExtendedLiquid.currencyMillions(totalAmount, false)}
                    </StyledTypography>
                  </Box>
                }
                sx={{
                  ml: 0,
                  width: '100%',
                  '& .MuiFormControlLabel-label': { width: '100%' },
                }}
              />
            );
          },
        )}
      </Box>

      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <StyledTypography sx={{ fontWeight: 600 }}>
          Selected Amount: {ExtendedLiquid.currencyMillions(selectedAmount, false)}
        </StyledTypography>
        <StyledTypography variant="body2" sx={{ color: 'text.secondary' }}>
          Total Committed:{' '}
          {ExtendedLiquid.currencyMillions(allocationsData.committedInvestment, false)}
        </StyledTypography>
      </Box>
    </InvestmentEntitySelectionWrapper>
  );
};

export default FundUpdateAllocationSelection;
